import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Handshake } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useFormalizarVenda, usePreviaMensagem } from "@/hooks/use-mercado";
import { mascararTelefoneBR, normalizarTelefoneBR, telefoneWhatsappValido } from "@shared/mercado";

/**
 * Formalizar um romaneio EMITIDO com o corretor — o primeiro elo do /mercado.
 *
 * ⚠️ Formalizar é ATO EXPLÍCITO: só o botão "Formalizar e enviar" deste modal cria
 * o compartilhamento. Nada aqui pode ser pendurado num `onSuccess` de emitir venda.
 *
 * ⚠️ O envio sai do WhatsApp DO PRODUTOR, e isso é decisão de produto, não limitação
 * do ciclo: o corretor conhece aquele número, e é isso que faz a mensagem ser lida.
 * Número novo da plataforma disparando nota comercial para desconhecido é o padrão de
 * maior risco de bloqueio no WhatsApp Business. Envio pela plataforma entra no C2,
 * quando o corretor tem conta e o opt-in existe.
 *
 * ⚠️ UM toque, não três passos. O WhatsApp da barra da venda abre a conversa e marca
 * como enviado no mesmo gesto — o app não tem como saber se a mensagem saiu, e pedir
 * uma confirmação separada só transferia burocracia para quem está no pátio com o
 * caminhão parado. Desistir na tela do WhatsApp é caso real: "Não enviei o link", no ⋯.
 *
 * O card que ficava AQUI (convite "Formalizar com X?" e, depois, "Enviar para o
 * comprador") saiu: a formalização virou uma etapa da linha do tempo da venda, e as
 * ações dela (Formalizar, WhatsApp, Copiar texto) moram na barra de cima.
 */

/** A prévia do texto como bolha de conversa — o que vai sair no WhatsApp. */
function bolhaWhatsApp(texto: string) {
  return (
    <div className="rounded-lg rounded-tl-sm bg-sucesso-suave text-foreground text-[13px] leading-relaxed p-3 whitespace-pre-wrap break-words border border-sucesso/20">
      {texto}
    </div>
  );
}

export function ModalFormalizar({
  vendaId, compradorNome, telefoneCadastro, vendaPaga, onFechar, onPronto,
}: {
  vendaId: string;
  compradorNome: string;
  telefoneCadastro?: string | null;
  /** Nota já quitada dispensa o vencimento — ninguém lembra o prazo de junho. */
  vendaPaga?: boolean;
  onFechar: () => void;
  onPronto: () => void;
}) {
  const { toast } = useToast();
  const [telefone, setTelefone] = useState(mascararTelefoneBR(telefoneCadastro ?? ""));
  const [vencimento, setVencimento] = useState("");
  const formalizar = useFormalizarVenda();

  const digitos = normalizarTelefoneBR(telefone);
  const telefoneOk = telefoneWhatsappValido(telefone);
  const previa = usePreviaMensagem(vendaId, vencimento, telefoneOk ? digitos : "", true);
  // Vencimento é obrigatório só para nota EM ABERTO. Em nota paga o campo é
  // irrelevante, e exigi-lo obrigaria o produtor a inventar a data de uma carga
  // antiga só para conseguir subir o histórico (o backfill do C2).
  const podeEnviar = telefoneOk && (!!vencimento || !!vendaPaga) && !formalizar.isPending;

  async function handleFormalizar() {
    try {
      await formalizar.mutateAsync({ vendaId, telefone: digitos, vencimento: vencimento || null });
      onPronto();
    } catch (e: any) {
      // Nunca engolir: 409 (já formalizada) e 422 (venda não emitida) precisam
      // chegar ao produtor com o motivo, não como um botão que não faz nada.
      toast({ title: "Não deu para formalizar", description: e.message, variant: "destructive" });
    }
  }

  return (
    <Dialog open onOpenChange={aberto => { if (!aberto) onFechar(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Formalizar com {compradorNome}</DialogTitle>
          <DialogDescription>
            Confira o texto e o prazo. Nada é enviado automaticamente — você abre o WhatsApp no próximo passo.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="telefone-comprador">WhatsApp do comprador</Label>
            <Input
              id="telefone-comprador"
              inputMode="numeric"
              placeholder="(74) 99999-8888"
              value={telefone}
              onChange={e => setTelefone(mascararTelefoneBR(e.target.value))}
              data-testid="input-telefone-comprador"
            />
            {telefone.length > 0 && !telefoneOk && (
              <p className="text-xs text-erro">Informe DDD + 9 dígitos.</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="vencimento-comprador">
              Vencimento{vendaPaga && <span className="text-muted-foreground font-normal"> (opcional)</span>}
            </Label>
            <Input
              id="vencimento-comprador"
              type="date"
              value={vencimento}
              onChange={e => setVencimento(e.target.value)}
              data-testid="input-vencimento-comprador"
            />
            <p className="text-xs text-muted-foreground">
              {vendaPaga
                ? "Esta venda já está paga — pode deixar em branco."
                : "Vai no texto e no link. É o prazo que o comprador vai ver."}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label>Como vai chegar</Label>
            {previa.isLoading && <p className="text-xs text-muted-foreground">Montando o texto…</p>}
            {previa.data && bolhaWhatsApp(previa.data.texto)}
            {/* O link aparece mascarado de propósito: o token só nasce quando você
                formaliza. Gerar um token para desenhar o preview deixaria um token
                órfão a cada vez que este modal abrisse. */}
            <p className="text-xs text-muted-foreground">
              O link é gerado quando você formalizar — por isso ele aparece mascarado aqui.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onFechar}>Cancelar</Button>
          <Button onClick={handleFormalizar} disabled={!podeEnviar} data-testid="button-formalizar-enviar">
            <Handshake className="h-4 w-4 mr-2" />
            {formalizar.isPending ? "Formalizando…" : "Formalizar e enviar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
